import { type EventBusKey, useEventBus } from "@vueuse/core";
import type { AxiosProgressEvent } from "axios";
import { reactive } from "vue";

import { notify } from "~/components/notifications";
import { NotificationType } from "~/shared/enums";
import type { StreamCallback } from "~/shared/types";
import { prettyFormatList } from "~/utils/common";

export const EVENT_BUS_FILE_UPLOAD_PROGRESS: EventBusKey<
  {
    duid: string;
  } & AxiosProgressEvent
> = Symbol("EVENT_BUS_FILE_UPLOAD_PROGRESS");

export const filePreviews = reactive(new Map<string, string>());
export const startFileUpload = (duid: string, file: File) => {
  filePreviews.set(duid, URL.createObjectURL(file));
};

export const finishFileUpload = (duid: string) => {
  const url = filePreviews.get(duid);
  if (!url) {
    return;
  }
  URL.revokeObjectURL(url);
  filePreviews.delete(duid);
};

export const makeFileUploadProgressTracker =
  (duid: string): StreamCallback =>
  (event) => {
    const eventBus = useEventBus(EVENT_BUS_FILE_UPLOAD_PROGRESS);
    eventBus.emit({ duid, ...event });
  };

export const partitionReadableFiles = async <T extends { file: File }>(items: T[]) => {
  const readability = await Promise.all(
    items.map(async (item) => {
      try {
        await item.file.slice(0, 1).arrayBuffer();
        return { item, isReadable: true };
      } catch {
        return { item, isReadable: false };
      }
    })
  );

  const unreadableFiles = readability.filter(({ isReadable }) => !isReadable).map(({ item }) => item.file);
  if (unreadableFiles.length > 0) {
    notify({
      message: `${prettyFormatList(unreadableFiles.map((file) => file.name))} can't be uploaded, ${unreadableFiles.length === 1 ? "it may be a bundle or package. Please zip it first." : "they may be bundles or packages. Please zip them first."}`,
      type: NotificationType.ERROR,
    });
  }

  return {
    readableFiles: readability.filter(({ isReadable }) => isReadable).map(({ item }) => item),
    unreadableFiles,
  };
};
